/**
 * VÉRAULT Member Suite — TripDestinationRibbon (Wave 26.24 · Phase 3b).
 * ────────────────────────────────────────────────────────────────────
 * Quiet horizontal beadline above the day stack showing the
 * multi-destination shape of the trip:
 *
 *     NEW YORK · JUN 2–7   ─→   ANTIGUA · JUN 7–16
 *
 * Renders only when:
 *   · The trip has ≥2 client-visible destination segments, AND
 *   · The trip is NOT veiled (reveal_destination === "full").
 *
 * When veiled, this surface stays completely silent — destination
 * leaks are not allowed.
 */
import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { fmt, api } from "../../../../index";

function shortDateRange(s, e) {
  if (!s) return "";
  try {
    const start = fmt.formatDate(s, { month: "short", day: "numeric" });
    if (!e) return start;
    return `${start}–${fmt.formatDate(e, { day: "numeric" })}`;
  } catch {
    return "";
  }
}

export function TripDestinationRibbon({ trip }) {
  const [segments, setSegments] = React.useState(null);

  React.useEffect(() => {
    if (!trip?.id) return undefined;
    const veiled = (trip.reveal_destination || "full") !== "full";
    if (veiled) { setSegments([]); return undefined; }
    let alive = true;
    api.get(`/client-portal/trips/${trip.id}/destinations`)
      .then(({ data }) => alive && setSegments(Array.isArray(data?.destinations) ? data.destinations : []))
      .catch(() => alive && setSegments([]));
    return () => { alive = false; };
  }, [trip?.id, trip?.reveal_destination]);

  // Silent on: loading, veiled, fewer than 2 segments.
  if (!segments) return null;
  const veiled = (trip?.reveal_destination || "full") !== "full";
  if (veiled) return null;
  if (segments.length < 2) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
      data-testid="trip-destination-ribbon"
      className="-mx-5 sm:mx-0 px-5 sm:px-0"
    >
      <ul className="flex items-center gap-2.5 sm:gap-4 overflow-x-auto no-scrollbar py-1.5">
        {segments.map((s, i) => (
          <React.Fragment key={s.id || `${s.destination_name}-${i}`}>
            <li
              className="shrink-0 px-3.5 py-1.5 rounded-full bg-white/70 backdrop-blur-sm border border-[rgba(0,0,0,0.05)] flex items-baseline gap-2"
              data-testid={`trip-dest-bead-${i}`}
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--foreground))]">
                {s.destination_name}
              </span>
              {(s.segment_start_date || s.segment_end_date) && (
                <span className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
                  {shortDateRange(s.segment_start_date, s.segment_end_date)}
                </span>
              )}
            </li>
            {i < segments.length - 1 && (
              <ArrowRight
                className="size-3 text-[hsl(var(--foreground-muted))] shrink-0"
                strokeWidth={1.5}
                aria-hidden
              />
            )}
          </React.Fragment>
        ))}
      </ul>
    </motion.div>
  );
}

export default TripDestinationRibbon;
